import React, { useEffect, useState, useRef, useMemo } from 'react';
import { db, auth } from '../services/firebase';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc
} from 'firebase/firestore';
import {
  MessageSquarePlus, LogOut, MoreVertical, Trash2, Edit2, MessageSquare, X, Check, Settings, Calendar, Loader2, WifiOff, AlertCircle, LayoutDashboard, ListChecks, Search, PanelLeftClose
} from 'lucide-react';
import { isPaidSubscriptionTier, isSubscriptionTier, SUBSCRIPTION_PLANS, SubscriptionTier } from '../config/subscriptionPlans';
import { useStripeSubscription } from '../hooks/useStripeSubscription';
import { CHAT_COLLECTION } from '../config/runtime';
import { matchesChatTitle } from '../utils/chatTitleSearch';
import type { MentionableChat } from '../shared/chatMentions';
import { deleteChat } from '../services/chatDeletionService';
import { useModalAccessibility } from '../hooks/useModalAccessibility';
import LogoutConfirmationDialog from './LogoutConfirmationDialog';
import { useUsagePreview } from '../features/usagePreview/context';
import { UsagePreviewMeter } from '../features/usagePreview/UsagePreviewMeter';
import { previewPlanName } from '../features/usagePreview/model';
import { useEconomicBilling } from '../features/economicBilling/context';
import { EconomicBillingMeter } from '../features/economicBilling/EconomicBillingMeter';
import { EconomicBillingPending } from '../features/economicBilling/EconomicBillingPending';
import {
  getChatDeletionAnnouncement,
  restoreChatsAfterFailedDeletion,
  takeChatsForOptimisticDeletion
} from '../utils/chatDeletionUiState';

interface SidebarProps {
  currentChatId: string | null;
  onSelectChat: (chatId: string) => void;
  onNewChat: () => void;
  isOpen: boolean;
  onCloseMobile: () => void;
  onOpenProfile: () => void;
  onOpenPlans: () => void;
  onOpenAdmin?: () => void;
  isOnline?: boolean;
  onChatsChange?: (chats: MentionableChat[]) => void;
  onChatsDeleted?: (ids: string[]) => void;
}

interface ChatSession {
  id: string;
  title: string;
  createdAt: any;
  updatedAt?: any;
}

interface UserUsageData {
  tier: SubscriptionTier;
  messagesCount: number;
  periodEnd: number;
}

const PAGE_SIZE = 30;
type ChatsLoadState = 'idle' | 'loading' | 'ready' | 'error';

const Sidebar: React.FC<SidebarProps> = ({ currentChatId, onSelectChat, onNewChat, isOpen, onCloseMobile, onOpenProfile, onOpenPlans, onOpenAdmin, isOnline = true, onChatsChange, onChatsDeleted }) => {
  const [chats, setChats] = useState<ChatSession[]>([]);
  const [chatsLoadState, setChatsLoadState] = useState<ChatsLoadState>('idle');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedChatIds, setSelectedChatIds] = useState<Set<string>>(new Set());
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [isDeleteSelectionDialogOpen, setIsDeleteSelectionDialogOpen] = useState(false);
  const [isDeletingSelection, setIsDeletingSelection] = useState(false);
  const [pendingDeletionIds, setPendingDeletionIds] = useState<string[]>([]);
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const [deletionAnnouncement, setDeletionAnnouncement] = useState('');
  const [usageData, setUsageData] = useState<UserUsageData>({ tier: 'free', messagesCount: 0, periodEnd: 0 });
  const preview = useUsagePreview();
  const billing = useEconomicBilling();
  const previewState = usageData.tier === 'admin' ? preview?.state : null;

  const menuRef = useRef<HTMLDivElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const asideRef = useRef<HTMLElement>(null);
  const longPressTimerRef = useRef<number | null>(null);
  const longPressTriggeredRef = useRef<number>(0);
  const hiddenChatIdsRef = useRef(new Set<string>());
  const deleteSelectionDialogRef = useRef<HTMLDivElement>(null);
  const cancelSelectionDeletionRef = useRef<HTMLButtonElement>(null);
  const deletionReturnFocusRef = useRef<HTMLElement | null>(null);
  const user = auth.currentUser;
  const loadedChatsUserIdRef = useRef<string | null>(null);
  const onChatsChangeRef = useRef(onChatsChange);
  onChatsChangeRef.current = onChatsChange;
  const { renewalDate, cancelAtPeriodEnd, hasPendingUpdate, loading: loadingSubscription } = useStripeSubscription(user?.uid);

  useModalAccessibility(deleteSelectionDialogRef, {
    enabled: isDeleteSelectionDialogOpen,
    onClose: isDeletingSelection ? undefined : cancelChatDeletion,
    initialFocusRef: cancelSelectionDeletionRef,
    restoreFocus: false
  });

  useEffect(() => { document.title = "Juris"; }, []);

  useEffect(() => {
    onChatsChangeRef.current?.(chatsLoadState === 'ready' && user?.uid === loadedChatsUserIdRef.current
      ? chats.map(({ id, title }) => ({ id, title })) : []);
  }, [chats, chatsLoadState, user?.uid]);

  useEffect(() => {
    loadedChatsUserIdRef.current = null;
    setChats([]);
    setChatsLoadState('idle');
    setVisibleCount(PAGE_SIZE);
    setSearchText('');
    setIsSearchOpen(false);
    setIsSelectionMode(false);
    setSelectedChatIds(new Set());
    setIsDeleteSelectionDialogOpen(false);
    setIsDeletingSelection(false);
    setPendingDeletionIds([]);
    setShowLogoutConfirmation(false);
    setLogoutError(null);
    setDeletionAnnouncement('');
    hiddenChatIdsRef.current.clear();
  }, [user?.uid]);

  useEffect(() => {
    if (!isOpen) {
      setIsSearchOpen(false);
      setSearchText('');
      clearSelection();
      setOpenMenuId(null);
      return;
    }
    setVisibleCount(PAGE_SIZE);
    clearSelection();
    setOpenMenuId(null);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [isOpen]);

  useEffect(() => {
    const aside = asideRef.current;
    if (!aside) return;

    if (isOpen) {
      aside.removeAttribute('inert');
    } else {
      aside.setAttribute('inert', '');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!user) return;
    setChatsLoadState('loading');
    const chatsQuery = query(collection(db, CHAT_COLLECTION), where('userId', '==', user.uid));
    const unsubscribe = onSnapshot(chatsQuery, (snapshot) => {
      loadedChatsUserIdRef.current = user.uid;
      const chatsData = snapshot.docs.map((docSnap) => {
        const data = docSnap.data() as ChatSession;
        const snapAny = docSnap as any;
        const fallbackTime = snapAny.updateTime?.toMillis
          ? snapAny.updateTime.toMillis()
          : (snapAny.createTime?.toMillis
            ? snapAny.createTime.toMillis()
            : (docSnap.metadata.hasPendingWrites ? Date.now() : 0));
        return { ...data, id: docSnap.id, _sortTime: fallbackTime };
      }) as Array<ChatSession & { _sortTime?: number }>;

      const getMillis = (value: any) => (value?.toMillis ? value.toMillis() : (typeof value === 'number' ? value : 0));

      chatsData.sort((a, b) => {
        const timeA = getMillis(a.updatedAt ?? a.createdAt) || a._sortTime || 0;
        const timeB = getMillis(b.updatedAt ?? b.createdAt) || b._sortTime || 0;
        return timeB - timeA;
      });

      const snapshotIds = new Set(chatsData.map((chat) => chat.id));
      for (const hiddenId of hiddenChatIdsRef.current) {
        if (!snapshotIds.has(hiddenId)) hiddenChatIdsRef.current.delete(hiddenId);
      }

      setChats(chatsData
        .filter((chat) => !hiddenChatIdsRef.current.has(chat.id))
        .map(({ _sortTime, ...chat }) => chat));
      setChatsLoadState('ready');
    }, (error) => {
      setChats([]);
      setChatsLoadState('error');
      if (error?.code === 'permission-denied' && localStorage.getItem('juris_account_deleted') === 'true') return;
      console.error("Error en listener de chats:", error);
    });
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setUsageData({
          tier: isSubscriptionTier(data.tier) ? data.tier : 'free',
          messagesCount: data.usage?.messagesCount || 0,
          periodEnd: data.usage?.periodEnd || 0
        });
      }
    }, (error) => {
      if (error?.code === 'permission-denied' && localStorage.getItem('juris_account_deleted') === 'true') return;
      console.error("Error en listener de uso:", error);
    });
    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    if (isLoggingOut || !showLogoutConfirmation) return;
    setIsLoggingOut(true);
    setLogoutError(null);
    try {
      await auth.signOut();
      setShowLogoutConfirmation(false);
    } catch {
      setLogoutError('No se pudo cerrar sesión. Inténtalo de nuevo.');
    } finally { setIsLoggingOut(false); }
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
    setSearchText('');
    setVisibleCount(PAGE_SIZE);
    window.requestAnimationFrame(() => searchButtonRef.current?.focus());
  };

  const startSelectionMode = (chatId: string) => {
    setIsSelectionMode(true);
    setSelectedChatIds(new Set([chatId]));
    setOpenMenuId(null);
  };

  const toggleSelection = (chatId: string) => {
    setSelectedChatIds((current) => {
      const next = new Set(current);
      if (next.has(chatId)) {
        next.delete(chatId);
      } else {
        next.add(chatId);
      }
      if (next.size === 0) setIsSelectionMode(false);
      return next;
    });
  };

  const clearSelection = () => {
    setIsSelectionMode(false);
    setSelectedChatIds(new Set());
  };

  const startPosRef = useRef<{x: number, y: number} | null>(null);

  const handlePointerDown = (e: React.PointerEvent, chatId: string) => {
    if (isSelectionMode) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    startPosRef.current = { x: e.clientX, y: e.clientY };
    longPressTriggeredRef.current = 0;
    longPressTimerRef.current = window.setTimeout(() => {
      longPressTriggeredRef.current = Date.now();
      startSelectionMode(chatId);
    }, 450);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!startPosRef.current || !longPressTimerRef.current) return;
    const dx = e.clientX - startPosRef.current.x;
    const dy = e.clientY - startPosRef.current.y;
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handlePointerUp = () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    startPosRef.current = null;
  };

  const handleChatClick = (e: React.MouseEvent, chatId: string) => {
    e.preventDefault();
    // Ignorar si ocurrió un long-press hace menos de 500ms
    if (Date.now() - longPressTriggeredRef.current < 500) {
      return;
    }
    if (isSelectionMode) {
      toggleSelection(chatId);
      return;
    }
    handleSelect(chatId);
  };

  const handleDeleteChat = (chatId: string) => {
    deletionReturnFocusRef.current = Array.from(listRef.current?.querySelectorAll<HTMLButtonElement>('[data-chat-id]') || [])
      .find((button) => button.dataset.chatId === chatId) || listRef.current;
    setOpenMenuId(null);
    setPendingDeletionIds([chatId]);
    setIsDeleteSelectionDialogOpen(true);
  };

  const requestDeleteSelected = () => {
    if (selectedChatIds.size === 0) return;
    deletionReturnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : listRef.current;
    setPendingDeletionIds(Array.from(selectedChatIds));
    setIsDeleteSelectionDialogOpen(true);
  };

  function cancelChatDeletion() {
    if (isDeletingSelection) return;
    setIsDeleteSelectionDialogOpen(false);
    window.requestAnimationFrame(() => {
      const target = deletionReturnFocusRef.current;
      if (target?.isConnected) target.focus({ preventScroll: true });
      else listRef.current?.focus({ preventScroll: true });
    });
  }

  const handleDeleteSelected = async () => {
    const selectedIds = pendingDeletionIds;
    if (!isDeleteSelectionDialogOpen || selectedIds.length === 0 || isDeletingSelection) return;

    const { remaining, removed } = takeChatsForOptimisticDeletion(chats, selectedIds);
    if (removed.length === 0) {
      setIsDeleteSelectionDialogOpen(false);
      clearSelection();
      return;
    }

    setIsDeletingSelection(true);
    selectedIds.forEach((chatId) => hiddenChatIdsRef.current.add(chatId));
    setChats(remaining);
    setDeletionAnnouncement('');
    setIsDeleteSelectionDialogOpen(false);
    clearSelection();
    if (currentChatId && selectedIds.includes(currentChatId)) onNewChat();
    window.setTimeout(() => listRef.current?.focus({ preventScroll: true }), 0);

    const results = await Promise.allSettled(selectedIds.map((chatId) => deleteChat(chatId)));
    const failedIds = new Set(results.flatMap((result, index) => (
      result.status === 'rejected' ? [selectedIds[index]] : []
    )));
    onChatsDeleted?.(selectedIds.filter(chatId => !failedIds.has(chatId)));
    failedIds.forEach((chatId) => hiddenChatIdsRef.current.delete(chatId));

    if (failedIds.size > 0) {
      const failedChats = removed.filter(({ chat }) => failedIds.has(chat.id));
      setChats((current) => restoreChatsAfterFailedDeletion(current, failedChats));
      console.error(`No se pudieron eliminar ${failedIds.size} chats seleccionados.`);
    }

    setDeletionAnnouncement(getChatDeletionAnnouncement(
      selectedIds.length - failedIds.size,
      failedIds.size
    ));
    setIsDeletingSelection(false);
  };

  const startEditing = (e: React.MouseEvent, chat: ChatSession) => {
    e.preventDefault(); e.stopPropagation();
    setEditingChatId(chat.id); setEditTitle(chat.title); setOpenMenuId(null);
  };

  const saveTitle = async (chatId: string) => {
    const nextTitle = editTitle.trim();
    if (nextTitle) {
      try {
        await updateDoc(doc(db, CHAT_COLLECTION, chatId), { title: nextTitle, titleManuallyEdited: true });
        setChats((current) => current.map(chat => chat.id === chatId ? { ...chat, title: nextTitle } : chat));
      } catch (e) {
        console.error(e);
      }
    }
    setEditingChatId(null);
  };

  const handleSelect = (chatId: string) => { onSelectChat(chatId); onCloseMobile(); };

  const loadMoreChats = () => {
    setVisibleCount((current) => Math.min(current + PAGE_SIZE, filteredChats.length));
  };

  const filteredChats = useMemo(() => chats.filter((chat) => matchesChatTitle(chat.title, searchText)), [chats, searchText]);
  const visibleChats = useMemo(() => filteredChats.slice(0, visibleCount), [filteredChats, visibleCount]);
  const hasMore = filteredChats.length > visibleCount;

  const maxMessages = SUBSCRIPTION_PLANS[usageData.tier]?.maxMessages || 10;
  const isUnlimited = usageData.tier === 'admin';
  const percentage = isUnlimited ? 0 : Math.min(100, (usageData.messagesCount / maxMessages) * 100);
  let progressColor = 'bg-action';
  if (percentage > 75) progressColor = 'bg-warning-soft';
  if (percentage >= 100) progressColor = 'bg-danger-action';
  const isPaidPlan = isPaidSubscriptionTier(usageData.tier);
  const planName = SUBSCRIPTION_PLANS[usageData.tier].name.replace(/^Plan\s+/u, '');
  const accountName = user?.email?.split('@')[0] || 'Cuenta';

  const getRenewalText = () => {
    if (usageData.tier === 'admin') return 'Indefinido';

    if (renewalDate && !loadingSubscription) {
      return new Date(renewalDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
    }

    if (usageData.periodEnd) {
      return new Date(usageData.periodEnd).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
    }

    return 'Indefinido';
  };

  const getCycleLabel = () => {
    if (cancelAtPeriodEnd) return 'Finaliza';
    if (hasPendingUpdate) return 'Cambia';
    return 'Renueva';
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm transition-opacity"
          onClick={onCloseMobile}
          aria-hidden="true"
        />
      )}

      <aside
        ref={asideRef}
        aria-label="Historial y cuenta"
        className={`fixed inset-y-0 left-0 z-[60] w-[280px] bg-panel border-r border-line transform transition-transform duration-300 ease-in-out flex flex-col h-[100dvh] shadow-xl ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >

        <div className="flex h-[calc(4.5rem+env(safe-area-inset-top,0px))] shrink-0 items-center justify-between border-b border-line px-4 pt-[env(safe-area-inset-top,0px)] md:h-20 md:pt-0">
          <h2 className="flex items-center gap-3 text-lg font-bold tracking-[.12em] text-juris">
            <img src="/juris-logo.png" width={40} height={40} className="h-10 w-10 object-contain" alt="" /> JURIS
          </h2>
          <div className="flex items-center">
            <button ref={searchButtonRef} type="button" onClick={() => { setIsSearchOpen((open) => !open); setSearchText(''); setVisibleCount(PAGE_SIZE); }} className="inline-flex h-11 w-11 items-center justify-center rounded-full text-muted hover:bg-raised hover:text-ink" aria-label="Buscar chats" aria-expanded={isSearchOpen} aria-controls="chat-title-search"><Search size={20} /></button>
            <button onClick={onCloseMobile} className="inline-flex h-11 w-11 items-center justify-center rounded-full text-muted hover:bg-raised hover:text-ink" aria-label="Plegar menú lateral"><PanelLeftClose size={22} /></button>
          </div>
        </div>

        {isSearchOpen && <div className="flex items-center gap-1 px-3 pt-3 motion-fade-through-fast">
          <input id="chat-title-search" type="search" autoFocus value={searchText} onChange={(event) => { setSearchText(event.target.value); setVisibleCount(PAGE_SIZE); }} onKeyDown={(event) => { if (event.key === 'Escape') { event.stopPropagation(); closeSearch(); } }} aria-label="Buscar palabras en los títulos de chat" placeholder="Buscar chats…" className="min-h-11 min-w-0 flex-1 rounded-xl border border-line bg-canvas px-3 text-sm text-ink placeholder:text-muted" />
          <button type="button" onClick={closeSearch} aria-label="Cerrar búsqueda de chats" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-muted hover:bg-raised hover:text-ink"><X size={18} aria-hidden="true" /></button>
        </div>}
        <div className="p-3">
          <button onClick={() => { onNewChat(); onCloseMobile(); }} className="w-full flex items-center gap-3 px-4 py-3 bg-action hover:bg-action-hover text-white rounded-xl shadow-md transition-all active:scale-95 font-medium">
            <MessageSquarePlus size={20} /> Nuevo Chat
          </button>
        </div>

        {isSelectionMode && (
          <div className="px-3 pb-2">
            <div className="flex items-center justify-between bg-juris-soft border border-line rounded-lg px-3 py-2 text-xs">
              <span className="font-semibold text-juris">{selectedChatIds.size} seleccionados</span>
              <div className="flex items-center gap-2">
                <button type="button" onClick={requestDeleteSelected} className="inline-flex min-h-11 items-center rounded-lg px-2 font-semibold text-danger transition hover:bg-danger-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger">
                  Eliminar
                </button>
                <button type="button" onClick={clearSelection} className="inline-flex min-h-11 items-center rounded-lg px-2 font-semibold text-muted transition hover:bg-panel focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris">
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        <div ref={listRef} tabIndex={-1} role="region" aria-label="Historial de chats" className="flex-1 overflow-y-auto px-3 py-2 no-scrollbar space-y-1 focus:outline-none">
          {visibleChats.length === 0 && (
            <div className="text-center text-muted text-sm mt-10 p-4">
              {chatsLoadState === 'loading' ? (
                <>
                  <Loader2 className="w-10 h-10 mx-auto mb-2 opacity-30 animate-spin" />
                  <p>Cargando historial...</p>
                </>
              ) : chatsLoadState === 'error' ? (
                <>
                  {isOnline ? (
                    <AlertCircle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  ) : (
                    <WifiOff className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  )}
                  <p>{isOnline ? 'No se pudo cargar el historial' : 'Sin conexion'}</p>
                  <p className="mt-1 text-xs text-muted">
                    {isOnline ? 'Prueba de nuevo en unos segundos.' : 'El historial volvera cuando se recupere Internet.'}
                  </p>
                </>
              ) : (
                <>
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-20" />
                  <p>{searchText.trim() ? 'No hay chats con esas palabras' : 'No tienes chats guardados'}</p>
                </>
              )}
            </div>
          )}

          {visibleChats.map(chat => (
            <div key={chat.id} className={`group relative flex items-center rounded-lg cursor-pointer transition-colors ${currentChatId === chat.id ? 'bg-juris-soft text-ink border border-line' : 'lg:hover:bg-raised text-ink border border-transparent'}`}>
              {editingChatId === chat.id ? (
                <div className="flex items-center w-full p-2" onClick={(e) => e.stopPropagation()}>
                  <input autoFocus aria-label="Nuevo titulo del chat" className="flex-1 bg-panel border border-line rounded px-2 py-1 text-sm outline-none" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') saveTitle(chat.id); if (e.key === 'Escape') setEditingChatId(null); }} onBlur={() => saveTitle(chat.id)} />
                  <button onClick={() => saveTitle(chat.id)} className="ml-2 w-11 h-11 flex items-center justify-center text-juris hover:bg-juris-soft rounded" aria-label="Guardar nombre del chat"><Check size={16} /></button>
                </div>
              ) : (
                <button
                  data-chat-id={chat.id}
                  onClick={(e) => handleChatClick(e, chat.id)}
                  onPointerDown={(e) => handlePointerDown(e, chat.id)}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerUp}
                  onPointerLeave={handlePointerUp}
                  aria-current={currentChatId === chat.id ? 'page' : undefined}
                  aria-pressed={isSelectionMode ? selectedChatIds.has(chat.id) : undefined}
                  className={`flex-1 flex items-center gap-3 px-3 py-3 text-sm text-left truncate active:bg-raised transition-colors rounded-lg ${selectedChatIds.has(chat.id) ? 'bg-juris-soft' : ''}`}
                >
                  {isSelectionMode && (
                    <span className={`w-4 h-4 rounded-full border flex items-center justify-center ${selectedChatIds.has(chat.id) ? 'bg-action border-juris text-white' : 'border-line'}`}>
                      {selectedChatIds.has(chat.id) && <Check size={11} />}
                    </span>
                  )}
                  <span className="truncate flex-1 font-medium">{chat.title}</span>
                </button>
              )}
              {!editingChatId && !isSelectionMode && (
                <div className="relative">
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpenMenuId(openMenuId === chat.id ? null : chat.id); }}
                    className={`w-11 h-11 mr-1 flex items-center justify-center rounded-full text-muted hover:text-ink hover:bg-raised transition-colors ${openMenuId === chat.id ? 'bg-raised text-ink' : ''}`}
                    aria-label={`Opciones de ${chat.title}`}
                    aria-expanded={openMenuId === chat.id}
                    aria-controls={openMenuId === chat.id ? `chat-options-${chat.id}` : undefined}
                  >
                    <MoreVertical size={16} />
                  </button>
                  {openMenuId === chat.id && (
                    <div id={`chat-options-${chat.id}`} ref={menuRef} role="group" aria-label={`Acciones para ${chat.title}`} className="absolute right-0 top-8 w-40 bg-panel rounded-xl shadow-lg border border-line z-50 overflow-hidden motion-fade-through-fast">
                      <button onClick={(e) => startEditing(e, chat)} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-ink hover:bg-canvas transition-colors text-left"><Edit2 size={14} /> Renombrar</button>
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); startSelectionMode(chat.id); }} className="w-full min-h-11 flex items-center gap-2 px-4 py-2.5 text-sm text-ink hover:bg-canvas transition-colors text-left"><ListChecks size={14} /> Seleccionar</button>
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); void handleDeleteChat(chat.id); }} className="w-full min-h-11 flex items-center gap-2 px-4 py-2.5 text-sm text-danger hover:bg-danger-soft transition-colors text-left"><Trash2 size={14} /> Eliminar</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {hasMore && (
            <button onClick={loadMoreChats} className="w-full min-h-11 py-2 text-xs text-juris hover:text-juris">
              Cargar más chats
            </button>
          )}
        </div>

        <div className="p-4 border-t border-line bg-canvas/50 flex flex-col gap-3">
          {usageData.tier === 'admin' && onOpenAdmin && (
            <button
              type="button"
              onClick={onOpenAdmin}
              className="w-full min-h-11 flex items-center justify-center gap-2 rounded-lg border border-line bg-panel px-4 py-2 text-sm font-bold text-ink hover:bg-juris-soft transition-colors"
            >
              <LayoutDashboard size={16} />
              Dashboard admin
            </button>
          )}
          {usageData.tier === 'admin' && preview && !previewState && (
            <button type="button" onClick={preview.enable} className="min-h-9 text-left text-xs text-muted hover:text-juris">Vista previa de consumo</button>
          )}
          {previewState ? <UsagePreviewMeter state={previewState} onManage={onOpenPlans} /> : !isUnlimited && billing?.displayStatus && billing.displayStatus.tier !== 'admin' ? <EconomicBillingMeter status={billing.displayStatus} onManage={onOpenPlans} /> : billing?.mode === 'loading' && !isUnlimited ? <EconomicBillingPending billing={billing} onManage={onOpenPlans} /> : billing?.mode === 'legacy' && !isUnlimited && (
            <button
              type="button"
              onClick={onOpenPlans}
              className="w-full text-left bg-panel p-2.5 rounded-lg border border-line shadow-sm hover:border-line hover:shadow-md transition-all"
            >
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-xs font-semibold text-muted uppercase tracking-wider">{planName}</span>
                <span className={`text-xs font-bold ${percentage >= 100 ? 'text-danger' : 'text-juris'}`}>{usageData.messagesCount} / {maxMessages}</span>
              </div>
              <div className="w-full bg-raised rounded-full h-2 overflow-hidden" role="progressbar" aria-label="Uso mensual de consultas" aria-valuemin={0} aria-valuemax={maxMessages} aria-valuenow={Math.min(usageData.messagesCount, maxMessages)}>
                <div className={`h-full rounded-full transition-all duration-500 ease-out ${progressColor}`} style={{ width: `${percentage}%` }}></div>
              </div>
                <div className="mt-2 flex items-center gap-1 text-[11px] text-muted">
                <Calendar size={12} /> {getCycleLabel()}: {getRenewalText()}
              </div>
              <div className="mt-2 pt-2 border-t border-line text-xs font-semibold text-juris">
                {isPaidPlan ? 'Gestionar suscripción' : 'Ver planes'}
              </div>
            </button>
          )}
          <div className="flex min-w-0 items-center gap-2">
            <div className="h-8 w-8 shrink-0 rounded-full bg-action text-white flex items-center justify-center font-bold text-sm overflow-hidden border border-line shadow-sm">
              {user?.photoURL ? <img src={user.photoURL} alt="Foto de perfil" className="w-full h-full object-cover" /> : user?.email?.[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-semibold text-ink">{accountName}</p>
              <p className="truncate text-xs text-muted">{previewState ? previewPlanName(previewState.tier).replace(/^Plan /u, '') : planName}</p>
            </div>
            <div className="flex shrink-0 items-center">
              <button type="button" onClick={onOpenProfile} className="flex h-11 w-11 items-center justify-center rounded-lg text-muted transition-colors hover:bg-panel hover:text-juris" aria-label="Abrir ajustes" title="Ajustes">
                <Settings size={18} aria-hidden="true" />
              </button>
              <button type="button" onClick={() => { setLogoutError(null); setShowLogoutConfirmation(true); }} className="flex h-11 w-11 items-center justify-center rounded-lg text-muted transition-colors hover:bg-panel hover:text-danger" aria-label="Cerrar sesión" title="Cerrar sesión">
                <LogOut size={18} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </aside>

      <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">{deletionAnnouncement}</p>

      {isDeleteSelectionDialogOpen && (
        <div
          className="fixed inset-0 z-[90] flex items-end justify-center bg-canvas/85 p-4 backdrop-blur-sm sm:items-center"
          onMouseDown={cancelChatDeletion}
        >
          <div
            ref={deleteSelectionDialogRef}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-selected-chats-title"
            aria-describedby="delete-selected-chats-description"
            tabIndex={-1}
            onMouseDown={(event) => event.stopPropagation()}
            className="w-full max-w-sm rounded-[1.75rem] border border-line/70 bg-panel p-6 shadow-2xl motion-micro-scale"
          >
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-danger-soft text-danger">
              <Trash2 size={22} aria-hidden="true" />
            </div>
            <h2 id="delete-selected-chats-title" className="text-xl font-extrabold text-ink">
              Eliminar {pendingDeletionIds.length} {pendingDeletionIds.length === 1 ? 'chat' : 'chats'}
            </h2>
            <p id="delete-selected-chats-description" className="mt-2 text-sm leading-relaxed text-muted">
              {pendingDeletionIds.length === 1 ? 'Se eliminará el chat seleccionado' : 'Se eliminarán todos los chats seleccionados'} y sus archivos asociados. Esta acción no se puede deshacer.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                ref={cancelSelectionDeletionRef}
                type="button"
                onClick={cancelChatDeletion}
                disabled={isDeletingSelection}
                className="min-h-11 flex-1 rounded-xl bg-raised px-4 font-bold text-ink transition hover:bg-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-juris focus-visible:ring-offset-2 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void handleDeleteSelected()}
                disabled={isDeletingSelection}
                className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-danger-action px-4 font-bold text-white transition hover:bg-danger-action focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger focus-visible:ring-offset-2 disabled:opacity-60"
              >
                {isDeletingSelection ? <Loader2 size={17} className="animate-spin" aria-hidden="true" /> : null}
                {isDeletingSelection ? 'Eliminando…' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showLogoutConfirmation && <LogoutConfirmationDialog pending={isLoggingOut} error={logoutError} onCancel={() => { if (!isLoggingOut) setShowLogoutConfirmation(false); }} onConfirm={() => void handleLogout()} />}
    </>
  );
};

export default Sidebar;
